import { useState } from "react";
import { CalcCard, NumField, ResultRow, PrimaryButton, Section, SaveButton } from "@/components/CalcUI";
import { useUnits } from "@/context/UnitsContext";
import { useUnitInputs, useOnUnitsChange } from "@/lib/units";
import { runCalc } from "@/lib/api";
import { toast } from "sonner";

export default function TubingMovement() {
  const { units } = useUnits();

  const [hooke, setHooke] = useState({ force: "5000", length: "10000", area: "3.62" });
  const [hookeOut, setHookeOut] = useState(null);

  const [piston, setPiston] = useState({ delta_pi: "1000", delta_po: "0", ai: "9.62", ao: "12.57", as_: "3.62", length: "10000" });
  const [pistonOut, setPistonOut] = useState(null);

  const [bal, setBal] = useState({ delta_pi_avg: "500", delta_po_avg: "0", ai: "9.62", ao: "12.57", length: "10000" });
  const [balOut, setBalOut] = useState(null);

  const [th, setTh] = useUnitInputs({ length: "10000", delta_temp: "50" }, { length: "length", delta_temp: "dtemp" });
  const [thOut, setThOut] = useState(null);
  useOnUnitsChange(() => setThOut(null));

  // Only the thermal card is unit-aware; Hooke/piston/ballooning are field-only.
  const run = async (calc, inp, setter) => {
    try {
      const r = await runCalc(calc, Object.fromEntries(Object.entries(inp).map(([k, v]) => [k, parseFloat(v)])), calc === "thermal" ? units : "field");
      setter(r);
    } catch (e) {
      toast.error("Invalid inputs");
    }
  };

  return (
    <div data-testid="tubing-movement-page">
      <Section
        title="Tubing Movement"
        subtitle="Length change of tubing/casing due to Hooke's law, piston (Δp end areas), ballooning (radial Poisson effect), and thermal expansion. Cross-section area A must match the tubing steel body."
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <CalcCard testid="hooke-card" title="Hooke's Law" formula="ΔL = F·L / (A·E) ; E=30e6 psi">
            <div className="grid grid-cols-3 gap-3">
              <NumField testid="hk-f" label="Force" unit="lbf" value={hooke.force} onChange={(v) => setHooke({ ...hooke, force: v })} />
              <NumField testid="hk-l" label="Length" unit="ft" value={hooke.length} onChange={(v) => setHooke({ ...hooke, length: v })} />
              <NumField testid="hk-a" label="Steel A" unit="in²" value={hooke.area} onChange={(v) => setHooke({ ...hooke, area: v })} />
            </div>
            <div className="mt-4 flex justify-between">
              <PrimaryButton testid="hk-run" onClick={() => run("hookes_law", hooke, setHookeOut)}>Calculate</PrimaryButton>
              <SaveButton getPayload={() => hookeOut && { category: "Tubing Movement", calculator: "hookes_law", inputs: hooke, outputs: hookeOut, units, default_name: "Hooke stretch" }} />
            </div>
            {hookeOut && <div className="mt-4 border-t border-[#334155] pt-3"><ResultRow label="Stretch" value={hookeOut.stretch_in} unit="in" emphasis /></div>}
          </CalcCard>

          <CalcCard testid="piston-card" title="Piston Effect" formula="ΔL = −L/(E·As)·[ΔPi·(Ai−As) − ΔPo·(Ao−As)]">
            <div className="grid grid-cols-3 gap-3">
              <NumField testid="p-dpi" label="ΔPi" unit="psi" value={piston.delta_pi} onChange={(v) => setPiston({ ...piston, delta_pi: v })} />
              <NumField testid="p-dpo" label="ΔPo" unit="psi" value={piston.delta_po} onChange={(v) => setPiston({ ...piston, delta_po: v })} />
              <NumField testid="p-len" label="Length" unit="ft" value={piston.length} onChange={(v) => setPiston({ ...piston, length: v })} />
              <NumField testid="p-ai" label="Ai" unit="in²" value={piston.ai} onChange={(v) => setPiston({ ...piston, ai: v })} />
              <NumField testid="p-ao" label="Ao" unit="in²" value={piston.ao} onChange={(v) => setPiston({ ...piston, ao: v })} />
              <NumField testid="p-as" label="As (steel)" unit="in²" value={piston.as_} onChange={(v) => setPiston({ ...piston, as_: v })} />
            </div>
            <div className="mt-4"><PrimaryButton testid="p-run" onClick={() => run("piston_effect", piston, setPistonOut)}>Calculate</PrimaryButton></div>
            {pistonOut && <div className="mt-4 border-t border-[#334155] pt-3"><ResultRow label="Length change" value={pistonOut.length_change_in} unit="in" emphasis /><div className="text-[10px] mono text-[#94a3b8] mt-1">{pistonOut.direction}</div></div>}
          </CalcCard>

          <CalcCard testid="bal-card" title="Ballooning" formula="ΔL = −2μL/E · (ΔPi·Ai − ΔPo·Ao)/(Ao−Ai)">
            <div className="grid grid-cols-3 gap-3">
              <NumField testid="b-dpi" label="ΔPi avg" unit="psi" value={bal.delta_pi_avg} onChange={(v) => setBal({ ...bal, delta_pi_avg: v })} />
              <NumField testid="b-dpo" label="ΔPo avg" unit="psi" value={bal.delta_po_avg} onChange={(v) => setBal({ ...bal, delta_po_avg: v })} />
              <NumField testid="b-len" label="Length" unit="ft" value={bal.length} onChange={(v) => setBal({ ...bal, length: v })} />
              <NumField testid="b-ai" label="Ai" unit="in²" value={bal.ai} onChange={(v) => setBal({ ...bal, ai: v })} />
              <NumField testid="b-ao" label="Ao" unit="in²" value={bal.ao} onChange={(v) => setBal({ ...bal, ao: v })} />
            </div>
            <div className="mt-4"><PrimaryButton testid="b-run" onClick={() => run("ballooning", bal, setBalOut)}>Calculate</PrimaryButton></div>
            {balOut && <div className="mt-4 border-t border-[#334155] pt-3"><ResultRow label="Length change" value={balOut.length_change_in} unit="in" emphasis /></div>}
          </CalcCard>

          <CalcCard testid="th-card" title="Thermal" formula={units === "field" ? "ΔL = α·L·ΔT ; α=6.9e-6/°F" : "ΔL = α·L·ΔT ; α=12.4e-6/°C"}>
            <div className="grid grid-cols-2 gap-3">
              <NumField testid="th-len" label="Length" unit={units === "field" ? "ft" : "m"} value={th.length} onChange={(v) => setTh({ ...th, length: v })} />
              <NumField testid="th-dt" label="ΔT" unit={units === "field" ? "°F" : "°C"} value={th.delta_temp} onChange={(v) => setTh({ ...th, delta_temp: v })} />
            </div>
            <div className="mt-4"><PrimaryButton testid="th-run" onClick={() => run("thermal", th, setThOut)}>Calculate</PrimaryButton></div>
            {thOut && <div className="mt-4 border-t border-[#334155] pt-3"><ResultRow label="Length change" value={thOut.length_change_in ?? thOut.length_change_m} unit={thOut.unit} emphasis /></div>}
          </CalcCard>
        </div>
      </Section>
    </div>
  );
}
